import { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Shield, Lock, Download, ArrowRight, CheckCircle,
  AlertTriangle, XCircle, Info, FileText, Loader2, ChevronRight
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Derived variable helpers ────────────────────────────────────────────────
function deriveEffortCost(pct) {
  if (pct >= 75) return { effort: "1–2 months", cost: "$10k–$25k" };
  if (pct >= 60) return { effort: "2–4 months", cost: "$20k–$50k" };
  if (pct >= 45) return { effort: "3–6 months", cost: "$40k–$80k" };
  return { effort: "6–12 months", cost: "$60k–$120k" };
}

function deriveComplexity(gov) {
  return { Entry: "Low", "Level 1": "Moderate", "Level 2": "High", "Level 3": "Very High" }[gov] || "Low";
}

// ── Small reusable components ────────────────────────────────────────────────
function RiskBadge({ risk, testId }) {
  const cfg = {
    Low:            "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
    Moderate:       "text-amber-400 bg-amber-400/10 border-amber-400/30",
    "Moderate-High":"text-orange-400 bg-orange-400/10 border-orange-400/30",
    High:           "text-red-400 bg-red-400/10 border-red-400/30",
    "Very High":    "text-red-500 bg-red-500/10 border-red-500/30",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-block px-3 py-1 rounded-sm text-xs font-['JetBrains_Mono',monospace] font-semibold border tracking-wider uppercase ${cfg[risk] || cfg.High}`}
    >
      {risk}
    </span>
  );
}

function LevelBadge({ level, testId }) {
  const cfg = {
    Entry:   "text-[#94a3b8] bg-[#1e3a5f] border-[#334155]",
    "Level 1":"text-sky-300 bg-sky-900/20 border-sky-700/30",
    "Level 2":"text-amber-300 bg-amber-900/20 border-amber-700/30",
    "Level 3":"text-red-300 bg-red-900/20 border-red-700/30",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-block px-3 py-1 rounded-sm text-xs font-['JetBrains_Mono',monospace] font-semibold border tracking-wider ${cfg[level] || cfg.Entry}`}
    >
      {level}
    </span>
  );
}

function SectionCard({ title, icon: Icon, children, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-[#112d4e] border border-[#1e3a5f] rounded-sm p-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-[#c9a84c]" />
        <h3 className="font-['Manrope',sans-serif] font-bold text-sm text-[#c9a84c] uppercase tracking-widest">
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

function TableRow({ label, value, valueNode }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[#1e3a5f] last:border-b-0">
      <span className="text-[#94a3b8] text-sm">{label}</span>
      {valueNode || <span className="font-['JetBrains_Mono',monospace] text-sm text-[#f1f5f9]">{value}</span>}
    </div>
  );
}

// ── Email gate modal ─────────────────────────────────────────────────────────
function EmailGate({ open, onClose, onSubmit, loading }) {
  const [form, setForm] = useState({ first_name: "", email: "", company_name: "" });
  const [err, setErr] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) { setErr("Please enter a valid email address."); return; }
    if (!form.first_name.trim() || !form.company_name.trim()) { setErr("All fields are required."); return; }
    setErr("");
    onSubmit(form);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#112d4e] border border-[#1e3a5f] text-[#f1f5f9] max-w-md rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-['Manrope',sans-serif] font-bold text-lg text-[#f1f5f9] flex items-center gap-2">
            <Lock size={16} className="text-[#c9a84c]" />
            Unlock Your Full Report
          </DialogTitle>
          <DialogDescription className="text-[#94a3b8] text-sm">
            Enter your details to access the full results including effort estimates, gap themes and PDF download.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <input
            data-testid="gate-first-name"
            placeholder="First Name"
            value={form.first_name}
            onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
            required
            className="w-full bg-[#0a1f33] border border-[#1e3a5f] rounded-sm px-4 py-2.5 text-sm text-[#f1f5f9] placeholder-[#334155] focus:outline-none focus:ring-1 focus:ring-[#c9a84c]"
          />
          <input
            data-testid="gate-email"
            type="text"
            placeholder="Work Email"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            required
            className="w-full bg-[#0a1f33] border border-[#1e3a5f] rounded-sm px-4 py-2.5 text-sm text-[#f1f5f9] placeholder-[#334155] focus:outline-none focus:ring-1 focus:ring-[#c9a84c]"
          />
          <input
            data-testid="gate-company"
            placeholder="Company Name"
            value={form.company_name}
            onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))}
            required
            className="w-full bg-[#0a1f33] border border-[#1e3a5f] rounded-sm px-4 py-2.5 text-sm text-[#f1f5f9] placeholder-[#334155] focus:outline-none focus:ring-1 focus:ring-[#c9a84c]"
          />
          {err && <p className="text-red-400 text-xs" data-testid="gate-error">{err}</p>}
          <button
            data-testid="gate-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#c9a84c] text-[#0a1f33] font-['Manrope',sans-serif] font-bold text-sm uppercase tracking-widest py-3 rounded-sm hover:bg-[#d4b460] transition-all disabled:opacity-60 mt-1"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <><ArrowRight size={14} />Unlock Full Report</>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Results page ────────────────────────────────────────────────────────
export default function Results() {
  const location = useLocation();
  const navigate = useNavigate();
  const { results, answers } = location.state || {};

  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [gateLoading, setGateLoading] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfFetching, setPdfFetching] = useState(false);

  const derived = useMemo(() => {
    if (!results) return {};
    const { effort, cost } = deriveEffortCost(results.readiness_percent);
    return {
      effort_estimate: effort,
      cost_estimate: cost,
      disp_complexity: deriveComplexity(results.governance_level),
    };
  }, [results]);

  if (!results) {
    return (
      <div className="min-h-screen bg-[#0a1f33] flex items-center justify-center text-[#f1f5f9]">
        <div className="text-center">
          <p className="text-[#94a3b8] mb-4">No assessment data found.</p>
          <button onClick={() => navigate("/assessment")}
            className="bg-[#c9a84c] text-[#0a1f33] font-bold text-sm px-6 py-2 rounded-sm">
            Start Assessment
          </button>
        </div>
      </div>
    );
  }

  const handleEmailSubmit = async (formData) => {
    setGateLoading(true);
    try {
      const payload = {
        ...formData,
        assessment_id: "DISP_QS_V1",
        answers,
        results: { ...results, ...derived },
      };
      const { data } = await axios.post(`${API}/submission`, payload);
      setSubmissionId(data.submission_id);
      setIsUnlocked(true);
      setShowGate(false);
      prefetchPdf(data.submission_id); // start fetching PDF in background
    } catch (e) {
      console.error(e);
    } finally {
      setGateLoading(false);
    }
  };

  // Pre-fetch PDF blob immediately after email gate — runs in the background
  // while the user reads sections 5 & 6, so it's ready when they click Download.
  const prefetchPdf = async (sid) => {
    setPdfFetching(true);
    try {
      const resp = await axios.get(`${API}/submission/${sid}/pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      setPdfBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      console.error("PDF prefetch failed:", e);
    } finally {
      setPdfFetching(false);
    }
  };

  // Synchronous — must stay synchronous to preserve browser user-gesture context.
  // async/await breaks Chrome/Opera's download permission check.
  const handleDownloadPdf = () => {
    if (!submissionId) return;
    window.open(`${API}/submission/${submissionId}/pdf`, "_blank");
  };

  const readinessPct = results.readiness_percent;
  const riskCommentary = {
    Low:           "Your organisation appears structurally aligned with DISP expectations.",
    Moderate:      "Your organisation shows foundational controls but some preparation may be required.",
    "Moderate-High": "Defence clarification requests may occur without structured preparation.",
    High:          "Significant governance and cybersecurity uplift is likely required.",
  };
  const complexityCommentary = {
    Low:       "Entry level membership requirements are typically limited in scope.",
    Moderate:  "Level 1 DISP membership introduces formal governance and personnel security expectations.",
    High:      "Level 2 membership requires significant cybersecurity and security governance maturity.",
    "Very High":"Level 3 membership introduces classified system handling and extensive compliance requirements.",
  };
  const fociCommentary = {
    Moderate: "Your FOCI profile warrants review. Foreign ownership or board representation may require additional DISP disclosure.",
    High:     "Elevated FOCI indicators present. Formal FOCI mitigation or additional scrutiny should be expected during any DISP application.",
  };

  return (
    <div className="min-h-screen bg-[#0a1f33] text-[#f1f5f9] font-['Inter',sans-serif] pb-16">
      {/* Header */}
      <div className="bg-[#112d4e] border-b border-[#1e3a5f] py-5 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield size={20} className="text-[#c9a84c]" />
            <span className="font-['Manrope',sans-serif] font-bold text-[#c9a84c] tracking-widest uppercase text-sm">
              DefencIQ™
            </span>
          </div>
          <div data-testid="results-header-badge" className="text-xs text-[#94a3b8] font-mono">
            DISP_QS_V1
          </div>
        </div>
      </div>

      {/* Hero score bar */}
      <div className="bg-[#112d4e] border-b border-[#1e3a5f] py-8 px-6 mb-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-['Manrope',sans-serif] font-extrabold text-3xl sm:text-4xl mb-1"
            data-testid="results-title">
            Your DISP Readiness Snapshot
          </h1>
          <p className="text-[#94a3b8] text-sm mb-6">Based on your assessment responses</p>
          <div className="flex flex-wrap gap-6 items-center">
            <div data-testid="readiness-score-display">
              <p className="text-[#94a3b8] text-xs uppercase tracking-widest mb-1">Readiness Score</p>
              <p className="font-['JetBrains_Mono',monospace] font-bold text-4xl text-[#c9a84c]">
                {readinessPct.toFixed(1)}%
              </p>
            </div>
            <div className="flex-1 min-w-[180px]">
              <div className="h-2 bg-[#1e3a5f] rounded-full overflow-hidden mb-2">
                <div
                  data-testid="readiness-bar"
                  className="h-full bg-[#c9a84c] rounded-full transition-all duration-700"
                  style={{ width: `${readinessPct}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-[#334155]">
                <span>0%</span><span>50%</span><span>100%</span>
              </div>
            </div>
            <div>
              <p className="text-[#94a3b8] text-xs uppercase tracking-widest mb-1">Submission Risk</p>
              <RiskBadge risk={results.submission_risk} testId="submission-risk-badge" />
            </div>
          </div>
        </div>
      </div>

      {/* Sections grid */}
      <div className="max-w-4xl mx-auto px-6 space-y-6">

        {/* Section 1 — Membership Structure */}
        <SectionCard title="Membership Structure" icon={Shield} testId="section-membership">
          <p className="text-sm text-[#94a3b8] font-['Manrope',sans-serif] font-semibold mb-4">
            Recommended DISP Membership Structure
          </p>
          <TableRow label="Personnel Security" valueNode={<LevelBadge level={results.personnel_level} testId="personnel-level" />} />
          <TableRow label="Physical Security"  valueNode={<LevelBadge level={results.physical_level}  testId="physical-level"  />} />
          <TableRow label="ICT Security"       valueNode={<LevelBadge level={results.ict_level}       testId="ict-level"       />} />
          <TableRow label="Governance"         valueNode={<LevelBadge level={results.governance_level} testId="governance-level" />} />
          <div className="mt-4 pt-4 border-t border-[#1e3a5f] flex items-center justify-between">
            <span className="text-[#94a3b8] text-sm">Estimated DISP Timeline</span>
            <span className="font-['JetBrains_Mono',monospace] text-sm text-[#c9a84c] font-semibold"
              data-testid="timeline-estimate">
              {results.timeline_estimate}
            </span>
          </div>
        </SectionCard>

        {/* Section 2 — DISP Complexity */}
        <SectionCard title="DISP Complexity" icon={Info} testId="section-complexity">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[#94a3b8] text-sm">Overall DISP Complexity</span>
            <RiskBadge risk={derived.disp_complexity} testId="complexity-badge" />
          </div>
          <p className="text-[#94a3b8] text-sm leading-relaxed">
            {complexityCommentary[derived.disp_complexity] || ""}
          </p>
        </SectionCard>

        {/* Section 3 — Readiness Assessment */}
        <SectionCard title="Readiness Assessment" icon={CheckCircle} testId="section-readiness">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div>
              <p className="text-[#94a3b8] text-xs mb-1">Readiness Score</p>
              <p className="font-['JetBrains_Mono',monospace] text-2xl font-bold text-[#f1f5f9]"
                data-testid="readiness-pct-detail">{readinessPct.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-[#94a3b8] text-xs mb-1">Submission Risk</p>
              <RiskBadge risk={results.submission_risk} testId="submission-risk-detail" />
            </div>
          </div>
          <p className="text-[#94a3b8] text-sm leading-relaxed">
            {riskCommentary[results.submission_risk] || ""}
          </p>
        </SectionCard>

        {/* Section 4 — FOCI Risk */}
        <SectionCard title="FOCI Risk" icon={AlertTriangle} testId="section-foci">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[#94a3b8] text-sm">FOCI Risk Rating</span>
            <RiskBadge risk={results.foci_risk} testId="foci-risk-badge" />
          </div>
          {(results.foci_risk === "Moderate" || results.foci_risk === "High") && (
            <p className="text-[#94a3b8] text-sm leading-relaxed">
              {fociCommentary[results.foci_risk]}
            </p>
          )}
          {results.foci_risk === "Low" && (
            <p className="text-[#94a3b8] text-sm">
              No significant FOCI indicators detected based on your responses.
            </p>
          )}
        </SectionCard>

        {/* Gate / Unlock */}
        {!isUnlocked ? (
          <div className="relative overflow-hidden rounded-sm border border-[#c9a84c]/30">
            {/* blurred preview */}
            <div className="pointer-events-none select-none blur-sm opacity-40 p-6 space-y-4 bg-[#112d4e]">
              <div className="h-4 w-48 bg-[#1e3a5f] rounded" />
              <div className="h-3 w-full bg-[#1e3a5f] rounded" />
              <div className="h-3 w-3/4 bg-[#1e3a5f] rounded" />
              <div className="h-4 w-36 bg-[#1e3a5f] rounded mt-4" />
              <div className="grid grid-cols-3 gap-3">
                {[0,1,2].map(i => <div key={i} className="h-16 bg-[#1e3a5f] rounded" />)}
              </div>
            </div>
            {/* overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0a1f33]/80 backdrop-blur-sm p-8 text-center">
              <Lock size={28} className="text-[#c9a84c] mb-3" />
              <p className="font-['Manrope',sans-serif] font-bold text-lg text-[#f1f5f9] mb-1">
                Full Report Locked
              </p>
              <p className="text-[#94a3b8] text-sm mb-5 max-w-sm">
                Enter your details to unlock effort estimates, gap themes and your PDF download.
              </p>
              <button
                data-testid="unlock-report-btn"
                onClick={() => setShowGate(true)}
                className="inline-flex items-center gap-2 bg-[#c9a84c] text-[#0a1f33] font-['Manrope',sans-serif] font-bold text-sm uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-[#d4b460] transition-all shadow-[0_0_20px_rgba(201,168,76,0.3)]"
              >
                <ArrowRight size={15} />
                Unlock Full Report
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Section 5 — Estimated Effort */}
            <SectionCard title="Estimated Effort" icon={FileText} testId="section-effort">
              <TableRow label="Implementation Effort" value={derived.effort_estimate} />
              <TableRow label="Estimated Investment"  value={derived.cost_estimate}  />
              <TableRow label="Submission Delay Risk"
                valueNode={<RiskBadge risk={results.submission_risk} testId="effort-risk-badge" />} />
            </SectionCard>

            {/* Section 6 — Gap Themes */}
            <SectionCard title="Gap Themes" icon={XCircle} testId="section-gaps">
              <p className="text-[#94a3b8] text-sm mb-4">
                Top priority areas identified from your Cyber & Governance assessment:
              </p>
              <div className="space-y-3">
                {[results.gap_theme_1, results.gap_theme_2, results.gap_theme_3].map((theme, i) => (
                  <div
                    key={i}
                    data-testid={`gap-theme-${i + 1}`}
                    className="flex items-center gap-3 p-3 bg-[#0a1f33] border border-[#1e3a5f] rounded-sm"
                  >
                    <span className="w-6 h-6 rounded-sm bg-[#c9a84c]/15 text-[#c9a84c] text-xs font-['JetBrains_Mono',monospace] font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-sm text-[#f1f5f9] font-medium">{theme}</span>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* PDF Download */}
            <div className="flex flex-col sm:flex-row items-center gap-4 p-5 bg-[#112d4e] border border-[#c9a84c]/30 rounded-sm">
              <div className="flex-1">
                <p className="font-['Manrope',sans-serif] font-bold text-[#f1f5f9]">
                  Download DISP Readiness Snapshot
                </p>
                <p className="text-[#94a3b8] text-sm mt-0.5">
                  5-page PDF report — executive summary, membership implications, effort estimate and next steps.
                </p>
              </div>
              {pdfBlobUrl ? (
                <a
                  data-testid="pdf-download-btn"
                  href={pdfBlobUrl}
                  download="DISP_Readiness_Report.pdf"
                  className="inline-flex items-center gap-2 bg-[#c9a84c] text-[#0a1f33] font-['Manrope',sans-serif] font-bold text-sm uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-[#d4b460] transition-all whitespace-nowrap shadow-[0_0_16px_rgba(201,168,76,0.25)]"
                >
                  <Download size={14} /> Download PDF
                </a>
              ) : (
                <button
                  data-testid="pdf-download-btn"
                  disabled
                  className="inline-flex items-center gap-2 bg-[#c9a84c]/60 text-[#0a1f33] font-['Manrope',sans-serif] font-bold text-sm uppercase tracking-widest px-6 py-3 rounded-sm whitespace-nowrap cursor-not-allowed"
                >
                  <Loader2 size={14} className="animate-spin" />
                  {pdfFetching ? "Preparing…" : "Download PDF"}
                </button>
              )}
            </div>
          </>
        )}

        {/* Restart */}
        <div className="text-center pt-4">
          <button
            data-testid="restart-btn"
            onClick={() => navigate("/assessment")}
            className="text-[#94a3b8] text-sm hover:text-[#c9a84c] transition-colors inline-flex items-center gap-1"
          >
            <ChevronRight size={13} className="rotate-180" />
            Start a new assessment
          </button>
        </div>
      </div>

      {/* Email gate */}
      <EmailGate
        open={showGate}
        onClose={() => setShowGate(false)}
        onSubmit={handleEmailSubmit}
        loading={gateLoading}
      />
    </div>
  );
}
